/**
 * Customer Portal (UI) - callback profile create/edit modal.
 *
 * This mirrors the Templates modal shell while exposing webhook auth fields based on
 * selected auth type so the form is easier to scan and complete.
 */
import type { Dispatch, FormEvent, SetStateAction } from 'react';
import type { CallbackProfileUpsertInput } from '../../types/portal';
import type { CallbackProfileDraft } from './webhookDrafts';

type CallbackProfileModalProps = {
  isOpen: boolean;
  draft: CallbackProfileDraft;
  saving: boolean;
  accountId: string | null;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  setDraft: Dispatch<SetStateAction<CallbackProfileDraft>>;
};

function isHmacAuth(authType: CallbackProfileUpsertInput['authType']): boolean {
  return authType === 'HMAC_SHA256' || authType === 'HMAC_SHA512';
}

export function CallbackProfileModal({ isOpen, draft, saving, accountId, onClose, onSubmit, setDraft }: CallbackProfileModalProps) {
  if (!isOpen) {
    return null;
  }

  const isEditing = Boolean(draft.profileId);
  const showBearer = draft.authType === 'BEARER';
  const showBasic = draft.authType === 'BASIC';
  const showApiKey = draft.authType === 'API_KEY';
  const showHmac = isHmacAuth(draft.authType);
  const secretPlaceholder = isEditing ? 'Leave blank to keep existing' : '';

  return (
    <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} role="dialog" aria-modal="true">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <form onSubmit={onSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">{isEditing ? 'Edit Callback Profile' : 'Create Callback Profile'}</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} disabled={saving} />
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-5">
                  <label className="form-label">Profile Name</label>
                  <input
                    className="form-control"
                    value={draft.name}
                    onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                    required
                  />
                </div>
                <div className="col-md-7">
                  <label className="form-label">Callback URL</label>
                  <input
                    className="form-control"
                    value={draft.url}
                    onChange={(event) => setDraft((current) => ({ ...current, url: event.target.value }))}
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Method</label>
                  <select
                    className="form-select"
                    value={draft.method}
                    onChange={(event) => setDraft((current) => ({ ...current, method: event.target.value as 'POST' | 'PUT' }))}
                  >
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Auth Type</label>
                  <select
                    className="form-select"
                    value={draft.authType}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        authType: event.target.value as CallbackProfileUpsertInput['authType']
                      }))
                    }
                  >
                    <option value="NONE">NONE</option>
                    <option value="BEARER">BEARER</option>
                    <option value="BASIC">BASIC</option>
                    <option value="API_KEY">API_KEY</option>
                    <option value="HMAC_SHA256">HMAC_SHA256</option>
                    <option value="HMAC_SHA512">HMAC_SHA512</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Content Type</label>
                  <input
                    className="form-control"
                    value={draft.contentType}
                    onChange={(event) => setDraft((current) => ({ ...current, contentType: event.target.value }))}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Description</label>
                  <input
                    className="form-control"
                    value={draft.description}
                    onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
                  />
                </div>

                {showBearer ? (
                  <div className="col-md-6">
                    <label className="form-label">Bearer Token</label>
                    <input
                      type="password"
                      className="form-control"
                      placeholder={secretPlaceholder}
                      value={draft.authBearerToken}
                      onChange={(event) => setDraft((current) => ({ ...current, authBearerToken: event.target.value }))}
                    />
                  </div>
                ) : null}

                {showBasic ? (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">Basic Username</label>
                      <input
                        className="form-control"
                        value={draft.authBasicUsername}
                        onChange={(event) => setDraft((current) => ({ ...current, authBasicUsername: event.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Basic Password</label>
                      <input
                        type="password"
                        className="form-control"
                        placeholder={secretPlaceholder}
                        value={draft.authBasicPassword}
                        onChange={(event) => setDraft((current) => ({ ...current, authBasicPassword: event.target.value }))}
                      />
                    </div>
                  </>
                ) : null}

                {showApiKey ? (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">API Key Header</label>
                      <input
                        className="form-control"
                        value={draft.authApiKeyHeader}
                        onChange={(event) => setDraft((current) => ({ ...current, authApiKeyHeader: event.target.value }))}
                        placeholder="X-Api-Key"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">API Key Value</label>
                      <input
                        type="password"
                        className="form-control"
                        placeholder={secretPlaceholder}
                        value={draft.authApiKeyValue}
                        onChange={(event) => setDraft((current) => ({ ...current, authApiKeyValue: event.target.value }))}
                      />
                    </div>
                  </>
                ) : null}

                {showHmac ? (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">HMAC Secret</label>
                      <input
                        type="password"
                        className="form-control"
                        placeholder={secretPlaceholder}
                        value={draft.authHmacSecret}
                        onChange={(event) => setDraft((current) => ({ ...current, authHmacSecret: event.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Signature Header</label>
                      <input
                        className="form-control"
                        value={draft.authHmacHeader}
                        onChange={(event) => setDraft((current) => ({ ...current, authHmacHeader: event.target.value }))}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Timestamp Header</label>
                      <input
                        className="form-control"
                        value={draft.authHmacTimestampHeader}
                        onChange={(event) => setDraft((current) => ({ ...current, authHmacTimestampHeader: event.target.value }))}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">ID Header</label>
                      <input
                        className="form-control"
                        value={draft.authHmacIdHeader}
                        onChange={(event) => setDraft((current) => ({ ...current, authHmacIdHeader: event.target.value }))}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Prefix</label>
                      <input
                        className="form-control"
                        value={draft.authHmacPrefix}
                        onChange={(event) => setDraft((current) => ({ ...current, authHmacPrefix: event.target.value }))}
                        placeholder="sha256="
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Include Timestamp</label>
                      <label className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={draft.authHmacIncludeTimestamp}
                          onChange={(event) => setDraft((current) => ({ ...current, authHmacIncludeTimestamp: event.target.checked }))}
                        />
                      </label>
                    </div>
                  </>
                ) : null}

                <div className="col-md-6">
                  <label className="form-label">Enabled</label>
                  <label className="form-check form-switch mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={draft.enabled}
                      onChange={(event) => setDraft((current) => ({ ...current, enabled: event.target.checked }))}
                    />
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary ms-auto" disabled={saving || !accountId}>
                {saving ? 'Saving...' : isEditing ? 'Update Profile' : 'Create Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
